import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import {
  CardBudget,
  CardBudgetSkeleton,
} from '@/common/components/CardBudget/CardBudget';
import { Grid } from '@/common/components/Grid/Grid';
import { MovementsList } from '@/common/components/MovementsList/MovementsList';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { Section } from '@/common/components/Section/Section';
import { Switch } from '@/common/components/Switch/Switch';
import { ROUTES } from '@/common/constants/routes';
import { useGetEmployeeBudgetChoices } from '@/common/integrations/menhir/employee/budgets/budgets.hooks';
import { useBudgetsStorage } from '@/common/integrations/menhir/employee/budgets/budgets.storage';
import { IEmployeeBudget } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import { useBudgetMovements } from '@/web/hooks/useMovements';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { useRouter } from 'expo-router';
import { BudgetsContextMenu } from '../components/BudgetsContextMenu/BudgetsContextMenu';
import { BudgetsApplicationsCard } from './BudgetsApplicationsCard/BudgetsApplicationsCard';
import { BudgetsBalanceCard } from './BudgetsBalanceCard/BudgetsBalanceCard';
import { useGetBudgetsByType } from './BudgetsHomePage.utils';

export function BudgetsHomePage() {
  usePageViewTracking('budgets_page');
  const { t } = useTranslation();
  const router = useRouter();
  const {
    features: { FF_BUDGET_WITH_APPLICATION },
  } = useGetFeatures();
  const [showExpired, setShowExpired] = useState(false);
  const { storage } = useBudgetsStorage();

  const {
    activeBudgets,
    applicableBudgets,
    expiredBudgets,
    filteredBudgets,
    isLoading: isLoadingBudgets,
  } = useGetBudgetsByType(showExpired);
  const { employeeBudgetChoices } = useGetEmployeeBudgetChoices();

  const { movements, isLoading: isLoadingMovements } = useBudgetMovements({
    perPage: 5,
  });

  const canApplyToBudgets =
    FF_BUDGET_WITH_APPLICATION && applicableBudgets.length > 0;

  const showExpiredToggle =
    activeBudgets.length > 0 && expiredBudgets.length > 0;

  const hasActiveBudgets = activeBudgets.length > 0;
  const hasOptimisticBudgets = storage.activeBudgetsCount > 0;
  const showBudgetsGrid = hasActiveBudgets || hasOptimisticBudgets;

  return (
    <PageLayout
      headerProps={{
        title: t('dashboard.budgets.title'),
        primaryAction: hasActiveBudgets ? (
          <BudgetsContextMenu
            budgets={employeeBudgetChoices}
            actionType="reimbursement"
          />
        ) : null,
      }}
    >
      <Grid columnBasis="basis-[30rem]" gap="sibling-card">
        <BudgetsBalanceCard />
      </Grid>
      {showBudgetsGrid && (
        <Section
          title={
            hasActiveBudgets
              ? t('dashboard.budgets.active_budgets')
              : t('dashboard.budgets.expired_budgets')
          }
          action={
            showExpiredToggle && (
              <Switch
                className="hidden md:flex"
                checked={showExpired}
                onChange={setShowExpired}
                variant="descriptive"
                label={t('dashboard.budgets.show_expired_budgets')}
              />
            )
          }
        >
          <BudgetsCardGrid
            budgets={filteredBudgets}
            loading={isLoadingBudgets}
            placeholderCount={storage.activeBudgetsCount}
          />
          {showExpiredToggle && !showExpired && (
            <Button
              className="md:hidden"
              variant="tertiary"
              size="sm"
              onPress={() => setShowExpired(!showExpired)}
            >
              {t('dashboard.budgets.show_expired_budgets')}
            </Button>
          )}
        </Section>
      )}
      {canApplyToBudgets && (
        <BudgetsApplicationsCard hasBudgets={hasActiveBudgets} />
      )}
      <Section
        title={t('general.labels.latest_activity')}
        action={
          <Button
            variant="tertiary"
            size="sm"
            onPress={() =>
              router.navigate({
                pathname: ROUTES.BUDGETS_ACTIVITY,
                params: {
                  product: 'budgets',
                },
              })
            }
          >
            {t('general.see_all')}
          </Button>
        }
      >
        <MovementsList
          movements={movements?.list ?? []}
          loading={isLoadingMovements}
        />
      </Section>
    </PageLayout>
  );
}

function BudgetsCardGrid({
  budgets,
  loading,
  placeholderCount = 2,
}: {
  budgets: IEmployeeBudget[];
  loading: boolean;
  placeholderCount?: number;
}) {
  if (loading) {
    return (
      <Grid columnBasis="basis-[30rem]" gap="sibling-card">
        {Array.from({ length: placeholderCount }).map((_, index) => (
          <CardBudgetSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  return (
    <Grid columnBasis="basis-[30rem]" gap="sibling-card">
      {budgets.map((budget) => (
        <CardBudget key={budget.id} employeeBudget={budget} />
      ))}
    </Grid>
  );
}
